import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { FolderKanban, PlusCircle, Globe, Coins, Activity, Crown, Settings, CreditCard, LifeBuoy, Shield, LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { AssistantWidget } from "@/components/AssistantWidget";

const NAV = [
  { to: "/app", label: "Mis proyectos", icon: FolderKanban, end: true, id: "projects" },
  { to: "/app/nuevo", label: "Crear nueva aplicación", icon: PlusCircle, id: "new" },
  { to: "/app/publicadas", label: "Aplicaciones publicadas", icon: Globe, id: "published" },
  { to: "/app/creditos", label: "Créditos disponibles", icon: Coins, id: "credits" },
  { to: "/app/consumo", label: "Historial de consumo", icon: Activity, id: "usage" },
  { to: "/app/plan", label: "Plan contratado", icon: Crown, id: "plan" },
  { to: "/app/configuracion", label: "Configuración", icon: Settings, id: "settings" },
  { to: "/app/facturacion", label: "Facturación", icon: CreditCard, id: "billing" },
  { to: "/app/soporte", label: "Soporte", icon: LifeBuoy, id: "support" },
];

function SideNav({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = user.role === "admin" ? [...NAV, { to: "/app/admin", label: "Administración", icon: Shield, id: "admin" }] : NAV;
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-white/5"><Logo to="/app" /></div>
      <nav className="flex-1 overflow-y-auto scroll-thin p-3 space-y-0.5">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`sidebar-${n.id}-link`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-blue-500/15 text-white border border-blue-500/30" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/5 space-y-3">
        <div className="rounded-xl border border-[#222B40] bg-[#111622] p-3" data-testid="sidebar-credits-box">
          <p className="text-[11px] uppercase tracking-widest text-slate-500">Créditos</p>
          <p className="font-display text-2xl font-bold text-white" data-testid="sidebar-credits-value">{user.credits}</p>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm text-white truncate" data-testid="sidebar-user-name">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn" title="Cerrar sesión"
            className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#0A0D14] flex">
      <aside className="hidden lg:block w-64 flex-shrink-0 border-r border-white/5 bg-[#0c1019] sticky top-0 h-screen"><SideNav /></aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-[#0c1019] border-r border-white/10"><SideNav onNavigate={() => setOpen(false)} /></div>
          <button className="flex-1 bg-black/60" onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X className="w-5 h-5 m-4 text-white" /></button>
        </div>
      )}
      <main className="flex-1 min-w-0">
        <div className="lg:hidden h-14 flex items-center justify-between px-4 border-b border-white/5">
          <Logo to="/app" />
          <button onClick={() => setOpen(true)} data-testid="mobile-nav-open"><Menu className="w-5 h-5 text-white" /></button>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10"><Outlet /></div>
      </main>
      <AssistantWidget />
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 rise">
      <div>
        {eyebrow && <p className="text-xs uppercase tracking-[0.22em] font-semibold text-cyan-400">{eyebrow}</p>}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-white tracking-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-slate-400 max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ ok, children, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-amber-400"}`} />{children}
    </span>
  );
}

export const Card = ({ className = "", children, ...p }) => (
  <div className={`rounded-2xl border border-[#222B40] bg-[#111622] p-6 ${className}`} {...p}>{children}</div>
);
